import React, { memo, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { NewOnboardingWizard } from 'src/components/payments-ops/bank-onboarding/NewOnboardingWizard'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { BackButton } from 'src/components/shared/BackButton'
import { NoAccessCard } from 'src/components/shared/NoAccessCard'
import { AdminFetchBankOnboardings, AdminStartBankOnboarding } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { PARTNERS_REQUIRED_MESSAGE } from 'src/constants/payments-ops/constants'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { BankOnboardingFormValues } from 'src/interfaces/bankOnboarding'
import {
  getBankOnboardings,
  getBankOnboardingsError,
  getBankOnboardingsLoadedKey,
  getBankOnboardingsLoading,
} from 'src/selectors/bankOnboarding'
import {
  buildBlockedPartnerReasons,
  buildEmptyOnboardingFormValues,
  buildOnboardingFormValuesFromPayload,
  buildStartBankOnboardingBody,
} from 'src/utils/paymentsOps/bankOnboardingForm'

/** The unfiltered list's key — partner blocking needs every partner's latest onboarding. */
const UNFILTERED_KEY = '|'

const NewBankOnboardingPageComponent = () => {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const [searchParams] = useSearchParams()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const onboardings = useSelector(getBankOnboardings)
  const loadedKey = useSelector(getBankOnboardingsLoadedKey)
  const isLoading = useSelector(getBankOnboardingsLoading)
  const listError = useSelector(getBankOnboardingsError)
  // The wizard's first step picks a partner from `GET /partners` (`partners_view`), which
  // PaymentsOpsLayout skips without it — nothing to pick, so the wizard is replaced by a
  // "No access" card and its supporting list is not fetched (spec §6, §10.2).
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  const fromId = searchParams.get('from')
  const hasUnfilteredList = loadedKey === UNFILTERED_KEY
  const hasListFailed = !hasUnfilteredList && !isLoading && listError !== null
  const hasWarnedRef = useRef<boolean>(false)

  // Needed for both pre-fill and partner blocking — load the UNFILTERED list on every visit. A held
  // copy may be stale (another operator may have started an onboarding since); the reducer keeps
  // it on screen while the refresh runs, and the submit re-validates against the refreshed one.
  useEffect(() => {
    if (!isAuthenticated || !canViewPartners) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchBankOnboardings.request({ search: null, status: null }))
  }, [asyncDispatchWithErrorToast, canViewPartners, isAuthenticated])

  const blockedPartnerReasons = useMemo(() => {
    return buildBlockedPartnerReasons(hasUnfilteredList ? onboardings : [])
  }, [hasUnfilteredList, onboardings])

  const sourceOnboarding = useMemo(() => {
    if (!canViewPartners || !fromId || !hasUnfilteredList) {
      return null
    }

    return (
      onboardings.find((onboarding) => {
        return onboarding.id === fromId
      }) ?? null
    )
  }, [canViewPartners, fromId, hasUnfilteredList, onboardings])

  // Pre-filling needs the list; a failed load falls back to an empty form instead of hanging.
  const isWaitingForSource = Boolean(fromId) && !hasUnfilteredList && !hasListFailed

  const initialValues = useMemo<BankOnboardingFormValues | null>(() => {
    if (isWaitingForSource) {
      return null
    }

    const today = dayjs()

    if (sourceOnboarding?.payload) {
      return buildOnboardingFormValuesFromPayload(sourceOnboarding.payload, today)
    }

    return buildEmptyOnboardingFormValues(today)
  }, [isWaitingForSource, sourceOnboarding])

  // An unknown or unreadable `from` falls back to an empty form — say so once.
  useEffect(() => {
    if (
      !canViewPartners ||
      !fromId ||
      isWaitingForSource ||
      sourceOnboarding?.payload ||
      hasWarnedRef.current
    ) {
      return
    }

    hasWarnedRef.current = true
    snackbar?.enqueueSnackbar(
      hasUnfilteredList && sourceOnboarding === null
        ? 'That onboarding was not found — starting from an empty form.'
        : 'That onboarding could not be pre-filled — starting from an empty form.',
      { variant: 'info' },
    )
  }, [canViewPartners, fromId, hasUnfilteredList, isWaitingForSource, snackbar, sourceOnboarding])

  const handleSubmit = useCallback(
    async (values: BankOnboardingFormValues): Promise<boolean> => {
      const result = await asyncDispatchWithErrorToast(
        AdminStartBankOnboarding.request(buildStartBankOnboardingBody(values)),
      )

      if (!result) {
        return false
      }

      snackbar?.enqueueSnackbar(
        `${values.legalName.trim()} onboarding started — it shows as Started while US Bank processes it.`,
        { variant: 'success' },
      )
      navigate(ADMIN_ROUTES.PAYMENTS_OPS_BANK_ONBOARDING_URL)

      return true
    },
    [asyncDispatchWithErrorToast, navigate, snackbar],
  )

  let body: ReactNode = (
    <Typography variant="body2" css={tw`text-shades-500`}>
      {isLoading ? 'Loading onboarding…' : 'Preparing form…'}
    </Typography>
  )

  if (!canViewPartners) {
    body = (
      <NoAccessCard
        message={PARTNERS_REQUIRED_MESSAGE}
        dataTestId="new-bank-onboarding-no-partner-access"
      />
    )
  } else if (initialValues) {
    body = (
      <NewOnboardingWizard
        key={`${fromId ?? 'new'}:${sourceOnboarding?.id ?? 'none'}`}
        initialValues={initialValues}
        blockedPartnerReasons={blockedPartnerReasons}
        onSubmit={handleSubmit}
      />
    )
  }

  return (
    <div
      data-testid="new-bank-onboarding-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <div css={tw`mb-4`}>
        <BackButton
          label="Back to Bank Onboarding"
          to={ADMIN_ROUTES.PAYMENTS_OPS_BANK_ONBOARDING_URL}
          dataTestId="new-bank-onboarding-back-button"
        />
      </div>
      <PageHeader
        title="New onboarding"
        description={
          sourceOnboarding?.payload
            ? `Pre-filled from ${sourceOnboarding.partnerName}'s closed onboarding. SSNs, EINs and dates of birth must be re-entered.`
            : 'Creates a live US Bank banking relationship for a partner.'
        }
      />
      {body}
    </div>
  )
}

export const NewBankOnboardingPage = memo(NewBankOnboardingPageComponent)
